import "./ChatBanner.less";
import { useAppDispatch, useAppSelector } from "../hooks";
import { PlatinumBotChatBannerMessage } from "./PlatinumChatBotBannerMessage";
import PlatinumChatWithRepBannerMessage from "./PlatinumChatWithRepBannerMessage";
import { useCallback, useEffect } from "react";
import { clearChatBanner, showChatBanner } from "../customerAppSlice";
import { ChatState } from "@Models/chat/ChatState";
import { PersistedMessageType } from "@Src/hubs/chat/dtos/PersistedMessageType";
import { ClientRole } from "@Src/hubs/chat/dtos/ClientRole";
import { ClientMessageType } from "@Src/hubs/chat/dtos/ClientMessageType";

export enum CustomerChatMessageEnum {
    NONE,
    PLATINUM_BOT_CHAT_BANNER_MESSAGE,
    PLATINUM_CHAT_WITH_REP_BANNER_MESSAGE,
}

const CustomerChatBanner = (): JSX.Element => {
    const { chatBannerState, operatorsOnline, chat, hasChatEnded, messages } =
        useAppSelector((state) => state.customerApp);

    const dispatch = useAppDispatch();

    function getMessage(): JSX.Element {
        switch (chatBannerState.chatBannerMessage) {
            case CustomerChatMessageEnum.PLATINUM_BOT_CHAT_BANNER_MESSAGE:
                return <PlatinumBotChatBannerMessage />;
            case CustomerChatMessageEnum.PLATINUM_CHAT_WITH_REP_BANNER_MESSAGE:
                return <PlatinumChatWithRepBannerMessage />;
            default:
                return <></>;
        }
    }

    const handlePlatinumBanner = useCallback((): void => {
        // If the chat has ended or the user is already talking to a rep, clear the banner
        if (chat?.state !== ChatState.TalkingToBot || hasChatEnded) {
            dispatch(clearChatBanner());
            return;
        }

        const customerMessageSent = messages.some(
            (x) =>
                x.clientRole === ClientRole.Customer &&
                ![
                    PersistedMessageType.UserJoinedChat,
                    PersistedMessageType.UserLeftChat,
                    ClientMessageType.ChatCreated,
                ].includes(x.messageType)
        );

        if (customerMessageSent) {
            dispatch(
                showChatBanner({
                    chatBannerMessage:
                        CustomerChatMessageEnum.PLATINUM_CHAT_WITH_REP_BANNER_MESSAGE,
                    // Make sure operators are online to allow for this banner to show
                    active: operatorsOnline,
                })
            );
        } else {
            dispatch(
                showChatBanner({
                    chatBannerMessage:
                        CustomerChatMessageEnum.PLATINUM_BOT_CHAT_BANNER_MESSAGE,
                    active: true,
                })
            );
        }
    }, [chat?.state, dispatch, hasChatEnded, messages, operatorsOnline]);

    useEffect(() => {
        if (chat?.isPlatinum) {
            handlePlatinumBanner();
        }
    }, [chat?.isPlatinum, handlePlatinumBanner]);

    return chatBannerState.active ? (
        <div className="chat-banner" data-testid="customer-chat-banner">
            {getMessage()}
        </div>
    ) : (
        <></>
    );
};

export default CustomerChatBanner;
